import type { ReasoningStepData } from '@/components/reasoning/ReasoningStepsList';

/**
 * Keeping the in-flight "partial steps" list current from two transports —
 * WebSocket `step.progress` events (one step at a time) and polling snapshots
 * (the whole list) — without re-rendering the chat on every no-op update.
 *
 * Both paths answer "did this step meaningfully change?" with the same
 * `isSameStepSnapshot`, so a field that matters to the UI cannot be compared
 * on one transport and ignored on the other.
 */

/**
 * Compact signature of a step's tool results capturing per-tool status
 * transitions (running → done / error), which leave the tool-results LENGTH
 * unchanged. Missing status normalizes to '' so legacy data is stable.
 */
export function toolStatusSignature(
  toolResults: ReasoningStepData['tool_results']
): string {
  if (!toolResults || toolResults.length === 0) {
    return '';
  }
  return toolResults
    .map(tr => {
      const status = tr.status ?? '';
      const hasResult =
        tr.result !== null && tr.result !== undefined ? 'r' : '';
      const hasError = tr.error ? 'e' : '';
      return `${status}${hasResult}${hasError}`;
    })
    .join('|');
}

/**
 * Signature of the step's attached artifacts / dynamic views. Dynamic views
 * include their rendered title (the steps list renders "Ahoy created {title}"),
 * so a title change on a view whose id is stable still counts as a change.
 */
function attachmentSignature(step: ReasoningStepData): string {
  const artifactIds = (step.artifacts ?? []).map(a => a.id).join(',');
  const views = (step.dynamic_views ?? [])
    .map(v => `${v.id}:${v.title}`)
    .join(',');
  return `${artifactIds}|${views}`;
}

/**
 * Whether two snapshots of a step render identically: same identity, stage,
 * status, finality, content, tool-result count and per-tool status, and the
 * same attached artifacts / dynamic views (the steps list renders view links).
 * Stage is compared because it drives the rendered label and icon, so a stage
 * change with unchanged status/content must still re-render. The tool-result
 * count is compared as well as the signature because an empty list and one
 * tool with no status share the signature ''.
 */
export function isSameStepSnapshot(
  a: ReasoningStepData,
  b: ReasoningStepData
): boolean {
  return (
    a.id === b.id &&
    a.stage === b.stage &&
    a.status === b.status &&
    a.is_final === b.is_final &&
    a.content === b.content &&
    (a.tool_results?.length ?? 0) === (b.tool_results?.length ?? 0) &&
    toolStatusSignature(a.tool_results) ===
      toolStatusSignature(b.tool_results) &&
    attachmentSignature(a) === attachmentSignature(b)
  );
}

/**
 * Apply one step update (WebSocket `step.progress`): replace the step with the
 * same id, or append a new one. Fields the incoming update does not carry
 * (a WS event has no artifacts or dynamic views) keep the existing step's
 * values rather than being dropped. Returns `prev` itself when nothing
 * changed, so the state setter bails out of a re-render.
 */
export function mergeStepProgress(
  prev: ReasoningStepData[],
  incoming: ReasoningStepData
): ReasoningStepData[] {
  const index = prev.findIndex(step => step.id === incoming.id);
  if (index === -1) {
    return [...prev, incoming];
  }
  const existing = prev[index];
  const merged: ReasoningStepData = {
    ...existing,
    ...incoming,
    tokens_used: incoming.tokens_used ?? existing.tokens_used,
    tool_results: incoming.tool_results ?? existing.tool_results,
    artifacts: incoming.artifacts ?? existing.artifacts,
    dynamic_views: incoming.dynamic_views ?? existing.dynamic_views,
  };
  if (isSameStepSnapshot(existing, merged)) {
    return prev;
  }
  const next = prev.slice();
  next[index] = merged;
  return next;
}

/**
 * Apply a polling snapshot of the whole list. Returns `prev` itself when every
 * step is unchanged (polls fire far more often than steps change), otherwise
 * the server's list.
 */
export function reconcilePolledSteps(
  prev: ReasoningStepData[],
  next: ReasoningStepData[]
): ReasoningStepData[] {
  const unchanged =
    prev.length === next.length &&
    prev.every((step, i) => isSameStepSnapshot(step, next[i]));
  return unchanged ? prev : next;
}
